import { useMutation, useQueryClient } from '@tanstack/vue-query';
import type { NotificationsListResponse } from '~/composables/useNotificationsQuery';

type ListPayload = { success: boolean; data: NotificationsListResponse } | undefined;

export const useMarkNotificationRead = () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      $fetch<{ success: boolean; data: { id: string; readAt: string } }>(
        `/api/notifications/${id}/read`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${authStore.token}` },
        },
      ),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'list'] });
      const snapshots = queryClient.getQueriesData<ListPayload>({ queryKey: ['notifications', 'list'] });

      const nowIso = new Date().toISOString();
      snapshots.forEach(([key, payload]) => {
        if (!payload?.data) return;
        const wasUnread = payload.data.items.some((n) => n.id === id && !n.readAt);
        queryClient.setQueryData<ListPayload>(key, {
          ...payload,
          data: {
            ...payload.data,
            items: payload.data.items.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: nowIso } : n)),
            unreadCount: wasUnread ? Math.max(0, payload.data.unreadCount - 1) : payload.data.unreadCount,
          },
        });
      });

      return { snapshots };
    },
    onError: (_error, _vars, context) => {
      context?.snapshots?.forEach(([key, payload]) => {
        queryClient.setQueryData(key, payload);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    },
  });
};
